/**
 * Brief パネル — キャンペーン詳細編集 (background_info) + 生成設定。
 * blur 時自動保存（IME 安全のため Enter キーでの保存はしない）。
 *
 * UIUX フロー:
 *   sections が空 (sourceCampaignId 未設定 or sections 0 件)
 *   → CampaignSelector + Brief 入力 + 「ランディングを生成」ボタン
 *   sections あり
 *   → キャンペーン詳細編集 + 「ページ全体を再生成」ボタン
 */
import {
  BACKGROUND_GROUP_PRESETS,
  CAMPAIGN_BACKGROUND_DEFAULT_KEYS,
  CAMPAIGN_BACKGROUND_PLACEHOLDERS,
  LANDING_DEFAULT_PROMPT_INSERTION,
  LANDING_INDUSTRY_LABELS,
  LANDING_INDUSTRY_RECIPES,
  LANDING_VISUAL_IDENTITIES,
  expandGroupPreset,
  generateBackgroundKey,
  getCampaignBackgroundLabel,
  resolveBackgroundLabel,
  type BackgroundFieldType,
  type BackgroundInfoValue,
  type CampaignBackgroundSchema,
  type LandingIndustryRecipe,
  type LandingVisualIdentity,
} from '@goenhub/shared';
import {
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Loader2,
  Plus,
  RefreshCw,
  Sparkles,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { AssignPanel } from './AssignPanel';
import { BrandPanel } from './BrandPanel';
import { HistoryPanel } from './HistoryPanel';
import {
  ReferenceFilesSection,
  toReferenceDocuments,
  type ReferenceFileItem,
} from './ReferenceFilesSection';

import { Button } from '@/components/ui/button';
import { Input, Label, Textarea } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/useAuth';
import {
  useCampaign,
  useCampaigns,
  useCreateCampaign,
  useUpdateCampaign,
} from '@/hooks/useCampaigns';
import {
  useGenerateLanding,
  useRegenerateLandingAllSections,
  useUpdateLanding,
  type LandingRecord,
} from '@/hooks/useLandings';

const NEW_CAMPAIGN_VALUE = '__new__';

interface Props {
  landingId: string;
  landing: LandingRecord;
}

export function BriefPanel({ landingId, landing }: Props) {
  const { user } = useAuth();
  const { data: campaign } = useCampaign(landing.sourceCampaignId || null);
  const { data: campaignsList } = useCampaigns();
  const createCampaign = useCreateCampaign();
  const updateCampaign = useUpdateCampaign();
  const updateLanding = useUpdateLanding();
  const regenAll = useRegenerateLandingAllSections();
  const generateLanding = useGenerateLanding();
  // 参考資料はブラウザ内だけで保持する（保存しない）
  const [references, setReferences] = useState<ReferenceFileItem[]>([]);
  const readingReferences = references.some((r) => r.status === 'reading');

  // sections が空 = まだ AI 生成していない初期状態
  const isInitial = (landing.sections?.length ?? 0) === 0;

  const usableCampaigns = useMemo(
    () => (campaignsList ?? []).filter((c) => !c.isTemplate),
    [campaignsList],
  );

  const handleSelectCampaign = async (value: string) => {
    if (value === NEW_CAMPAIGN_VALUE) {
      try {
        const id = await createCampaign.mutateAsync({
          name: '(未命名キャンペーン)',
          status: 'planning',
          createdBy: user?.uid ?? '',
        });
        await updateLanding.mutateAsync({
          id: landingId,
          data: {
            sourceCampaignId: id,
            promptInsertion: LANDING_DEFAULT_PROMPT_INSERTION,
          },
        });
        toast.success('新規キャンペーンを作成しました');
      } catch (e) {
        toast.error(`キャンペーン作成失敗: ${e instanceof Error ? e.message : String(e)}`);
      }
    } else {
      await updateLanding.mutateAsync({
        id: landingId,
        data: {
          sourceCampaignId: value,
          promptInsertion: {
            ...LANDING_DEFAULT_PROMPT_INSERTION,
            ...(landing.promptInsertion ?? {}),
          },
        },
      });
    }
  };

  const handleGenerate = async () => {
    if (!landing.sourceCampaignId) {
      toast.error('キャンペーンを選択してください');
      return;
    }
    if (!landing.brand) {
      toast.error('ブランド名を入力してください');
      return;
    }
    try {
      await generateLanding.mutateAsync({
        landingId,
        sourceCampaignId: landing.sourceCampaignId,
        promptInsertion: landing.promptInsertion,
        brand: landing.brand,
        industry: landing.industry,
        generationMode: landing.generationMode ?? 'recipe',
        visualIdentity: landing.visualIdentity,
        domain: landing.domain ?? '',
        referenceDocuments: toReferenceDocuments(references),
      });
      toast.success('ランディング生成完了');
    } catch (e) {
      toast.error(`生成失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  return (
    <div className="p-3">
      <Tabs defaultValue="brief">
        <TabsList className="w-full">
          <TabsTrigger value="brief" className="flex-1 text-xs">
            概要
          </TabsTrigger>
          <TabsTrigger value="brand" className="flex-1 text-xs">
            ブランド
          </TabsTrigger>
          <TabsTrigger value="assign" className="flex-1 text-xs">
            連携
          </TabsTrigger>
          <TabsTrigger value="history" className="flex-1 text-xs">
            履歴
          </TabsTrigger>
        </TabsList>

        <TabsContent value="brief" className="space-y-3 mt-3">
          {/* キャンペーン選択 */}
          <div className="text-[11px] font-semibold text-[var(--text-muted)] tracking-wide">
            キャンペーン
          </div>
          <Select value={landing.sourceCampaignId || ''} onValueChange={handleSelectCampaign}>
            {/* 長いキャンペーン名が 2 行に折り返して読みづらかったため 1 行省略に固定 */}
            <SelectTrigger
              className="whitespace-nowrap [&>span]:truncate [&>span]:block [&>span]:min-w-0"
              title={usableCampaigns.find((c) => c.id === landing.sourceCampaignId)?.name}
            >
              <SelectValue placeholder="キャンペーンを選択 or 新規作成" />
            </SelectTrigger>
            <SelectContent>
              {usableCampaigns.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
              <SelectItem value={NEW_CAMPAIGN_VALUE}>+ 新規キャンペーンを作成</SelectItem>
            </SelectContent>
          </Select>
          {createCampaign.isPending && (
            <p className="text-[10px] text-[var(--text-muted)] flex items-center gap-1">
              <Loader2 className="size-2.5 animate-spin" /> キャンペーン作成中...
            </p>
          )}

          {/* キャンペーン未選択時のヒント */}
          {!landing.sourceCampaignId && (
            <div className="text-xs text-[var(--text-muted)] p-3 border border-dashed border-[var(--border)] rounded-md text-center">
              キャンペーンを選択するとプロンプト入力フィールドが表示されます。
            </div>
          )}

          {/* キャンペーン詳細フォーム */}
          {landing.sourceCampaignId && (
            <CampaignBackgroundForm
              landingId={landingId}
              landing={landing}
              campaign={campaign}
              onTogglePromptInsertion={(key, value) =>
                updateLanding.mutateAsync({
                  id: landingId,
                  data: {
                    promptInsertion: {
                      ...landing.promptInsertion,
                      [key]: value,
                    },
                  },
                })
              }
              onCampaignFieldUpdate={(key, value) =>
                updateCampaign.mutateAsync({
                  id: landing.sourceCampaignId!,
                  data: { backgroundInfoPatch: { [key]: value } },
                })
              }
              onCampaignSchemaUpdate={(schema) =>
                updateCampaign.mutateAsync({
                  id: landing.sourceCampaignId!,
                  data: { background_info_schema: schema },
                })
              }
              onCampaignBatchUpdate={(fieldPatch, schema) =>
                updateCampaign.mutateAsync({
                  id: landing.sourceCampaignId!,
                  data: {
                    backgroundInfoPatch: fieldPatch,
                    background_info_schema: schema,
                  },
                })
              }
            />
          )}

          {/* 参考資料（PDF / Word / Excel / CSV） */}
          <ReferenceFilesSection
            items={references}
            onChange={setReferences}
            disabled={generateLanding.isPending || regenAll.isPending}
          />

          {/* デザイン設定 */}
          <div className="text-[11px] font-semibold text-[var(--text-muted)] tracking-wide pt-3 border-t border-[var(--border)]">
            デザイン設定
          </div>
          <div>
            <Label className="text-xs">ブランド名</Label>
            <BlurInput
              value={landing.brand}
              onSave={(v) => updateLanding.mutateAsync({ id: landingId, data: { brand: v } })}
            />
          </div>

          <div>
            <Label className="text-xs">設計モード</Label>
            <div className="flex gap-1 mt-1">
              <ModeToggleButton
                active={(landing.generationMode ?? 'recipe') === 'recipe'}
                onClick={() =>
                  updateLanding.mutateAsync({
                    id: landingId,
                    data: { generationMode: 'recipe' },
                  })
                }
                title="業界レシピの sequence を厳格に使う"
              >
                業界レシピ
              </ModeToggleButton>
              <ModeToggleButton
                active={landing.generationMode === 'ai_design'}
                onClick={() =>
                  updateLanding.mutateAsync({
                    id: landingId,
                    data: { generationMode: 'ai_design' },
                  })
                }
                title="AI が sequence と variant を自由に設計"
              >
                AI 設計
              </ModeToggleButton>
            </div>
            <p className="text-[10px] text-[var(--text-muted)] mt-1">
              {(landing.generationMode ?? 'recipe') === 'recipe'
                ? '業界レシピで定められたセクション順序を厳守します。'
                : 'AI がブランドに最適なセクションと順序を設計します。業界はヒントのみ。'}
            </p>
          </div>

          <div>
            <Label className="text-xs">
              業界{landing.generationMode === 'ai_design' && ' (ヒント)'}
            </Label>
            <Select
              value={landing.industry}
              onValueChange={(v) =>
                updateLanding.mutateAsync({
                  id: landingId,
                  data: { industry: v as LandingIndustryRecipe },
                })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANDING_INDUSTRY_RECIPES.map((r) => (
                  <SelectItem key={r} value={r}>
                    {LANDING_INDUSTRY_LABELS[r]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">ビジュアルテーマ</Label>
            <div className="grid grid-cols-5 gap-1.5 mt-1">
              {LANDING_VISUAL_IDENTITIES.map((id) => (
                <button
                  key={id}
                  type="button"
                  onClick={() =>
                    updateLanding.mutateAsync({
                      id: landingId,
                      data: { visualIdentity: id as LandingVisualIdentity },
                    })
                  }
                  className={`aspect-square rounded-md border-2 flex items-center justify-center ${
                    landing.visualIdentity === id
                      ? 'border-[var(--accent)] ring-2 ring-[var(--accent)] ring-offset-1'
                      : 'border-[var(--border)]'
                  } ${identityPreviewClass(id)}`}
                  title={IDENTITY_LABEL[id] ?? id}
                >
                  <span className="text-xs font-semibold">Aa</span>
                </button>
              ))}
            </div>
            <p className="text-[10px] text-[var(--text-muted)] mt-1">
              {IDENTITY_LABEL[landing.visualIdentity] ?? landing.visualIdentity}
            </p>
          </div>

          {/* 初回生成 or 全体再生成
              項目数が多いとパネルが長く、毎回最下部までスクロールする必要があったため
              パネル下端に貼り付ける (sticky)。 */}
          <div className="sticky bottom-0 -mx-3 px-3 pt-3 pb-1 bg-[var(--bg-surface)] border-t border-[var(--border)]">
            {isInitial ? (
              <Button
                className="w-full"
                disabled={
                  generateLanding.isPending ||
                  readingReferences ||
                  !landing.sourceCampaignId ||
                  !landing.brand
                }
                onClick={handleGenerate}
              >
                {generateLanding.isPending ? (
                  <>
                    <Loader2 className="size-3.5 mr-1 animate-spin" /> 生成中... (最大 3 分)
                  </>
                ) : (
                  <>
                    <Sparkles className="size-3.5 mr-1" /> ランディングを生成
                  </>
                )}
              </Button>
            ) : (
              <Button
                className="w-full"
                disabled={regenAll.isPending || readingReferences}
                onClick={async () => {
                  if (!confirm('全セクションのテキストを再生成しますか？')) return;
                  try {
                    await regenAll.mutateAsync({
                      landingId,
                      referenceDocuments: toReferenceDocuments(references),
                    });
                    toast.success('全セクション再生成完了');
                  } catch (e) {
                    toast.error(`再生成失敗: ${e instanceof Error ? e.message : String(e)}`);
                  }
                }}
              >
                {regenAll.isPending ? (
                  <>
                    <Loader2 className="size-3.5 mr-1 animate-spin" /> 再生成中...
                  </>
                ) : (
                  <>
                    <RefreshCw className="size-3.5 mr-1" /> ページ全体を再生成
                  </>
                )}
              </Button>
            )}
          </div>
        </TabsContent>

        <TabsContent value="brand" className="space-y-3 mt-3">
          <BrandPanel landingId={landingId} landing={landing} />
        </TabsContent>

        <TabsContent value="assign" className="space-y-3 mt-3">
          <AssignPanel landingId={landingId} landing={landing} />
        </TabsContent>

        <TabsContent value="history" className="space-y-2 mt-3">
          <HistoryPanel landingId={landingId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ModeToggleButton({
  active,
  onClick,
  title,
  children,
}: {
  active: boolean;
  onClick: () => void;
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`flex-1 text-xs px-3 py-2 rounded-md border transition-colors ${
        active
          ? 'bg-[var(--accent)] text-white border-[var(--accent)]'
          : 'bg-[var(--bg-elevated)] border-[var(--border)] hover:bg-[var(--bg-hover)] text-[var(--text-muted)]'
      }`}
    >
      {children}
    </button>
  );
}

const IDENTITY_LABEL: Record<string, string> = {
  light: 'ライト',
  dark: 'ダーク',
  vibrant: 'ヴィブラント',
  pastel: 'パステル',
  editorial: 'エディトリアル',
  mono: 'モノクロ',
  mint: 'ミント',
  azure: 'アジュール',
  terra: 'テラコッタ',
  noir: 'ノワール',
};

function identityPreviewClass(id: string): string {
  switch (id) {
    case 'light':
      return 'bg-white text-black';
    case 'dark':
      return 'bg-[#0B0D10] text-white';
    case 'vibrant':
      return 'bg-gradient-to-br from-purple-700 to-pink-500 text-white';
    case 'pastel':
      return 'bg-[#FAF4EE] text-[#3A2A1F]';
    case 'editorial':
      return 'bg-[#F8F5EE] text-black';
    case 'mono':
      return 'bg-white text-black border-2 border-black';
    case 'mint':
      return 'bg-[#F4FBF8] text-[#0FB28A]';
    case 'azure':
      return 'bg-[#F5F8FC] text-[#2563EB]';
    case 'terra':
      return 'bg-[#FBF6F0] text-[#C2552A]';
    case 'noir':
      return 'bg-[#0A0A0A] text-[#C9A45C]';
    default:
      return '';
  }
}

interface CampaignBackgroundFormProps {
  landingId: string;
  landing: LandingRecord;
  campaign: any;
  onTogglePromptInsertion: (key: string, value: boolean) => Promise<unknown>;
  onCampaignFieldUpdate: (key: string, value: BackgroundInfoValue) => Promise<unknown>;
  onCampaignSchemaUpdate: (schema: CampaignBackgroundSchema) => Promise<unknown>;
  onCampaignBatchUpdate: (
    fieldPatch: Record<string, BackgroundInfoValue>,
    schema: CampaignBackgroundSchema,
  ) => Promise<unknown>;
}

function CampaignBackgroundForm({
  landingId: _landingId,
  landing,
  campaign,
  onTogglePromptInsertion,
  onCampaignFieldUpdate,
  onCampaignSchemaUpdate,
  onCampaignBatchUpdate,
}: CampaignBackgroundFormProps) {
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  // OFF の項目は畳むが、値を直したいときのために手動で開けるようにする。
  const [openWhenOff, setOpenWhenOff] = useState<Record<string, boolean>>({});

  if (!campaign) return null;

  const bg: Record<string, BackgroundInfoValue> = {
    description: campaign.description ?? '',
    goal: campaign.goal ?? '',
    targetSegment: campaign.targetSegment ?? '',
    budget: campaign.budget ?? null,
    kpi: campaign.kpi ?? '',
    owner: campaign.owner ?? '',
    memo: campaign.memo ?? '',
    ...((campaign.background_info as Record<string, BackgroundInfoValue>) ?? {}),
  };
  const schema: CampaignBackgroundSchema =
    (campaign.background_info_schema as CampaignBackgroundSchema) ?? {};

  const orderIdx = new Map<string, number>();
  (schema.fieldOrder ?? []).forEach((k, i) => orderIdx.set(k, i));
  const customKeys = Object.keys(bg).filter(
    (k) => !(CAMPAIGN_BACKGROUND_DEFAULT_KEYS as readonly string[]).includes(k),
  );
  const standaloneKeys = customKeys
    .filter((k) => !schema.fields?.[k]?.groupId)
    .sort((a, b) => {
      const ia = orderIdx.has(a) ? orderIdx.get(a)! : Number.MAX_SAFE_INTEGER;
      const ib = orderIdx.has(b) ? orderIdx.get(b)! : Number.MAX_SAFE_INTEGER;
      return ia - ib;
    });

  const renameField = async (key: string, label: string) => {
    if ((CAMPAIGN_BACKGROUND_DEFAULT_KEYS as readonly string[]).includes(key)) return;
    const newSchema: CampaignBackgroundSchema = {
      ...schema,
      fields: {
        ...(schema.fields ?? {}),
        [key]: { ...(schema.fields?.[key] ?? {}), label },
      },
    };
    await onCampaignSchemaUpdate(newSchema);
  };

  const renameGroup = async (groupId: string, label: string) => {
    const newSchema: CampaignBackgroundSchema = {
      ...schema,
      groups: {
        ...(schema.groups ?? {}),
        [groupId]: { ...(schema.groups?.[groupId] ?? { label: '' }), label },
      },
    };
    await onCampaignSchemaUpdate(newSchema);
  };

  const removeField = async (key: string) => {
    if ((CAMPAIGN_BACKGROUND_DEFAULT_KEYS as readonly string[]).includes(key)) {
      toast.error('既定フィールドは削除できません');
      return;
    }
    const newFields = { ...(schema.fields ?? {}) };
    delete newFields[key];
    const newSchema: CampaignBackgroundSchema = {
      ...schema,
      fields: newFields,
      fieldOrder: (schema.fieldOrder ?? []).filter((k) => k !== key),
    };
    await onCampaignBatchUpdate({ [key]: null }, newSchema);
  };

  const addStandaloneField = async (type: BackgroundFieldType) => {
    const key = generateBackgroundKey('f');
    const defaultLabel =
      type === 'long'
        ? '長文項目'
        : type === 'number'
          ? '数値項目'
          : type === 'date'
            ? '日付項目'
            : '新しい項目';
    const initial: BackgroundInfoValue = type === 'number' ? null : '';
    const newSchema: CampaignBackgroundSchema = {
      ...schema,
      fields: {
        ...(schema.fields ?? {}),
        [key]: { label: defaultLabel, type },
      },
      fieldOrder: [...(schema.fieldOrder ?? []), key],
    };
    await onCampaignBatchUpdate({ [key]: initial }, newSchema);
    setAddMenuOpen(false);
  };

  const addGroup = async (presetId: string) => {
    const preset = BACKGROUND_GROUP_PRESETS[presetId];
    if (!preset) return;
    const existingCount = Object.values(schema.groups ?? {}).filter(
      (g) => g.presetId === presetId,
    ).length;
    const groupLabel = `${preset.label}${existingCount + 1}`;
    const expanded = expandGroupPreset(presetId, groupLabel);
    if (!expanded) return;
    const newSchema: CampaignBackgroundSchema = {
      ...schema,
      groups: { ...(schema.groups ?? {}), [expanded.groupId]: expanded.groupMeta },
      groupOrder: [...(schema.groupOrder ?? []), expanded.groupId],
      fields: {
        ...(schema.fields ?? {}),
        ...Object.fromEntries(expanded.fields.map((f) => [f.key, f.meta])),
      },
    };
    const valuePatch: Record<string, BackgroundInfoValue> = {};
    for (const f of expanded.fields) valuePatch[f.key] = f.initialValue;
    await onCampaignBatchUpdate(valuePatch, newSchema);
    setAddMenuOpen(false);
  };

  const removeGroup = async (groupId: string) => {
    const keysInGroup = Object.keys(schema.fields ?? {}).filter(
      (k) => schema.fields?.[k]?.groupId === groupId,
    );
    const newFields = { ...(schema.fields ?? {}) };
    for (const k of keysInGroup) delete newFields[k];
    const newGroups = { ...(schema.groups ?? {}) };
    delete newGroups[groupId];
    const newSchema: CampaignBackgroundSchema = {
      ...schema,
      fields: newFields,
      groups: newGroups,
      groupOrder: (schema.groupOrder ?? []).filter((g) => g !== groupId),
    };
    const valuePatch: Record<string, BackgroundInfoValue> = {};
    for (const k of keysInGroup) valuePatch[k] = null;
    await onCampaignBatchUpdate(valuePatch, newSchema);
  };

  const defaultKeys = CAMPAIGN_BACKGROUND_DEFAULT_KEYS.filter((k) => k in bg);
  const allKeys = [
    ...defaultKeys,
    ...standaloneKeys,
    ...Object.keys(schema.fields ?? {}).filter((k) => schema.fields?.[k]?.groupId && k in bg),
  ];
  const onCount = allKeys.filter((k) => !!landing.promptInsertion?.[k]).length;

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-[11px] font-semibold text-[var(--text-muted)] tracking-wide">
          AI に渡す情報
        </div>
        <div className="text-[10px] text-[var(--text-muted)]">
          <span className="text-[var(--accent)] font-semibold">{onCount}</span> / {allKeys.length}{' '}
          項目
        </div>
      </div>
      <p className="text-[10px] text-[var(--text-muted)] -mt-1.5 leading-relaxed">
        スイッチが ON の項目だけが、生成プロンプトに差し込まれます。OFF
        の項目は畳まれます（値は消えません）。
      </p>

      {/* 既定フィールド */}
      {defaultKeys.map((key) => {
        const value = bg[key];
        const isNumber = key === 'budget';
        const isLong = ['description', 'goal', 'targetSegment', 'kpi', 'memo'].includes(key);
        const placeholder =
          CAMPAIGN_BACKGROUND_PLACEHOLDERS[key as keyof typeof CAMPAIGN_BACKGROUND_PLACEHOLDERS];
        const on = !!landing.promptInsertion?.[key];
        const expanded = on || !!openWhenOff[key];
        return (
          <PromptField
            key={key}
            label={getCampaignBackgroundLabel(key)}
            on={on}
            expanded={expanded}
            value={value}
            onToggle={(v) => onTogglePromptInsertion(key, v)}
            onToggleExpand={() => setOpenWhenOff((s) => ({ ...s, [key]: !s[key] }))}
          >
            {isNumber ? (
              <BlurInput
                type="number"
                value={value === null || value === undefined ? '' : String(value)}
                placeholder={placeholder}
                onSave={(v) => onCampaignFieldUpdate(key, v === '' ? null : Number.parseInt(v, 10))}
              />
            ) : isLong ? (
              <BlurTextarea
                value={typeof value === 'string' ? value : ''}
                placeholder={placeholder}
                onSave={(v) => onCampaignFieldUpdate(key, v)}
              />
            ) : (
              <BlurInput
                value={value === null || value === undefined ? '' : String(value)}
                placeholder={placeholder}
                onSave={(v) => onCampaignFieldUpdate(key, v)}
              />
            )}
          </PromptField>
        );
      })}

      {/* 任意 standalone フィールド */}
      {standaloneKeys.length > 0 && (
        <div className="pt-1 border-t border-[var(--border)] space-y-2">
          <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] pt-1">
            任意フィールド
          </div>
          {standaloneKeys.map((key) => {
            const value = bg[key];
            const fieldMeta = schema.fields?.[key];
            const type: BackgroundFieldType = fieldMeta?.type ?? 'text';
            const label = resolveBackgroundLabel(key, schema);
            const on = !!landing.promptInsertion?.[key];
            const expanded = on || !!openWhenOff[key];
            return (
              <div
                key={key}
                className={`rounded-md border px-2 py-1.5 ${
                  on
                    ? 'border-[var(--border)] bg-[var(--bg-elevated)]'
                    : 'border-transparent bg-[var(--bg-root)]'
                }`}
              >
                <div className="flex items-center gap-1">
                  <Switch
                    checked={on}
                    onChange={(e) => onTogglePromptInsertion(key, e.target.checked)}
                    title={on ? 'プロンプトに含める（ON）' : 'プロンプトから除外（OFF）'}
                  />
                  <div className="flex-1">
                    <BlurInput
                      value={label}
                      onSave={(v) => renameField(key, v)}
                      placeholder="フィールド名（日本語可）"
                    />
                  </div>
                  {!on && (
                    <button
                      type="button"
                      onClick={() => setOpenWhenOff((st) => ({ ...st, [key]: !st[key] }))}
                      className="text-[10px] text-[var(--text-muted)] hover:text-[var(--accent)] px-1"
                    >
                      {expanded ? '閉じる' : '編集'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => removeField(key)}
                    className="text-[12px] text-[var(--text-muted)] hover:text-[var(--accent-red)] px-1"
                    title="削除"
                  >
                    ✕
                  </button>
                </div>
                {!expanded ? (
                  <p className="text-[10px] text-[var(--text-muted)] truncate mt-0.5 pl-9">
                    {value === null || value === undefined || value === ''
                      ? '未入力'
                      : String(value).replace(/\s+/g, ' ').trim()}
                  </p>
                ) : type === 'number' ? (
                  <BlurInput
                    type="number"
                    value={value === null || value === undefined ? '' : String(value)}
                    onSave={(v) =>
                      onCampaignFieldUpdate(key, v === '' ? null : Number.parseFloat(v))
                    }
                  />
                ) : type === 'long' ? (
                  <BlurTextarea
                    value={typeof value === 'string' ? value : ''}
                    onSave={(v) => onCampaignFieldUpdate(key, v)}
                  />
                ) : (
                  <BlurInput
                    type={type === 'date' ? 'date' : 'text'}
                    value={value === null || value === undefined ? '' : String(value)}
                    onSave={(v) => onCampaignFieldUpdate(key, v)}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* グループ */}
      {(schema.groupOrder ?? []).map((groupId) => {
        const group = schema.groups?.[groupId];
        if (!group) return null;
        const collapsed = collapsedGroups[groupId] ?? false;
        const groupKeys = Object.keys(bg).filter((k) => schema.fields?.[k]?.groupId === groupId);
        return (
          <div
            key={groupId}
            className="border border-[var(--border)] rounded-md bg-[var(--bg-elevated)]"
          >
            <div className="flex items-center gap-1 px-2 py-1.5 border-b border-[var(--border)]">
              <button
                type="button"
                onClick={() => setCollapsedGroups((c) => ({ ...c, [groupId]: !collapsed }))}
                className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                title={collapsed ? '展開' : '折りたたみ'}
              >
                {collapsed ? (
                  <ChevronRight className="size-3.5" />
                ) : (
                  <ChevronDown className="size-3.5" />
                )}
              </button>
              <div className="flex-1">
                <BlurInput
                  value={group.label}
                  onSave={(v) => renameGroup(groupId, v)}
                  placeholder="グループ名"
                />
              </div>
              {group.presetId && (
                <button
                  type="button"
                  onClick={() => addGroup(group.presetId!)}
                  className="text-[10px] text-[var(--text-muted)] hover:text-[var(--text-primary)] px-1.5 py-0.5 border border-[var(--border)] rounded whitespace-nowrap"
                  title="同じ種類をもう1セット追加"
                >
                  複製
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  if (
                    window.confirm(`グループ「${group.label}」と内部フィールドを削除しますか？`)
                  ) {
                    removeGroup(groupId);
                  }
                }}
                className="text-[12px] text-[var(--text-muted)] hover:text-[var(--accent-red)] px-1"
                title="グループを削除"
              >
                ✕
              </button>
            </div>
            {!collapsed && (
              <div className="p-2 space-y-2">
                {groupKeys.map((key) => {
                  const value = bg[key];
                  const fieldMeta = schema.fields?.[key];
                  const type: BackgroundFieldType = fieldMeta?.type ?? 'text';
                  const label = resolveBackgroundLabel(key, schema);
                  const on = !!landing.promptInsertion?.[key];
                  const expanded = on || !!openWhenOff[key];
                  return (
                    <div key={key} className={on ? '' : 'opacity-70'}>
                      <div className="flex items-center gap-1">
                        <Switch
                          checked={on}
                          onChange={(e) => onTogglePromptInsertion(key, e.target.checked)}
                          title={on ? 'プロンプトに含める（ON）' : 'プロンプトから除外（OFF）'}
                        />
                        <div className="flex-1">
                          <BlurInput
                            value={label}
                            onSave={(v) => renameField(key, v)}
                            placeholder="項目名"
                          />
                        </div>
                        {!on && (
                          <button
                            type="button"
                            onClick={() => setOpenWhenOff((st) => ({ ...st, [key]: !st[key] }))}
                            className="text-[10px] text-[var(--text-muted)] hover:text-[var(--accent)] px-1"
                          >
                            {expanded ? '閉じる' : '編集'}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => removeField(key)}
                          className="text-[11px] text-[var(--text-muted)] hover:text-[var(--accent-red)] px-1"
                          title="この項目を削除"
                        >
                          ✕
                        </button>
                      </div>
                      {!expanded ? (
                        <p className="text-[10px] text-[var(--text-muted)] truncate mt-0.5 pl-9">
                          {value === null || value === undefined || value === ''
                            ? '未入力'
                            : String(value).replace(/\s+/g, ' ').trim()}
                        </p>
                      ) : type === 'number' ? (
                        <BlurInput
                          type="number"
                          value={value === null || value === undefined ? '' : String(value)}
                          onSave={(v) =>
                            onCampaignFieldUpdate(key, v === '' ? null : Number.parseFloat(v))
                          }
                        />
                      ) : type === 'long' ? (
                        <BlurTextarea
                          value={typeof value === 'string' ? value : ''}
                          onSave={(v) => onCampaignFieldUpdate(key, v)}
                        />
                      ) : (
                        <BlurInput
                          type={type === 'date' ? 'date' : 'text'}
                          value={value === null || value === undefined ? '' : String(value)}
                          onSave={(v) => onCampaignFieldUpdate(key, v)}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* + 追加メニュー */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setAddMenuOpen((o) => !o)}
          className="w-full text-xs text-[var(--text-muted)] border border-dashed border-[var(--border)] rounded-md py-2 hover:bg-[var(--bg-hover)] transition-colors flex items-center justify-center gap-1"
        >
          <Plus className="size-3.5" />
          フィールドまたはグループを追加
          {addMenuOpen ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
        </button>
        {addMenuOpen && (
          <div className="mt-1 border border-[var(--border)] rounded-md bg-[var(--bg-elevated)] shadow-md overflow-hidden">
            <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-[var(--text-muted)] bg-[var(--bg-root)]">
              単一フィールド
            </div>
            <button
              type="button"
              onClick={() => addStandaloneField('text')}
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-[var(--bg-hover)]"
            >
              テキスト（1行）
            </button>
            <button
              type="button"
              onClick={() => addStandaloneField('long')}
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-[var(--bg-hover)]"
            >
              長文テキスト
            </button>
            <button
              type="button"
              onClick={() => addStandaloneField('number')}
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-[var(--bg-hover)]"
            >
              数値
            </button>
            <button
              type="button"
              onClick={() => addStandaloneField('date')}
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-[var(--bg-hover)]"
            >
              日付
            </button>
            <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-[var(--text-muted)] bg-[var(--bg-root)] border-t border-[var(--border)]">
              グループ（一括追加）
            </div>
            {Object.values(BACKGROUND_GROUP_PRESETS).map((preset) => (
              <button
                key={preset.id}
                type="button"
                onClick={() => addGroup(preset.id)}
                className="w-full text-left px-3 py-1.5 text-xs hover:bg-[var(--bg-hover)] flex items-center justify-between"
              >
                <span>{preset.label}</span>
                <span className="text-[10px] text-[var(--text-muted)]">
                  {preset.fields.length}項目
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * プロンプト差し込み項目の 1 行 (2026-07-28)。
 *
 * ON  … ラベル + 入力欄を展開
 * OFF … 入力欄を畳み、現在値を 1 行プレビューで見せる（クリックで編集用に展開）
 *
 * 旧 UI は OFF でも空の入力欄が同じ高さで居座り、「使われない項目」だと
 * 分からないうえ縦幅を圧迫していた。畳むことで ON の項目だけが並ぶ。
 */
function PromptField({
  label,
  on,
  expanded,
  value,
  onToggle,
  onToggleExpand,
  children,
}: {
  label: string;
  on: boolean;
  expanded: boolean;
  value: BackgroundInfoValue;
  onToggle: (v: boolean) => void;
  onToggleExpand: () => void;
  children: React.ReactNode;
}) {
  const preview =
    value === null || value === undefined || value === ''
      ? '未入力'
      : String(value).replace(/\s+/g, ' ').trim();

  return (
    <div
      className={`rounded-md border px-2 py-1.5 transition-colors ${
        on
          ? 'border-[var(--border)] bg-[var(--bg-elevated)]'
          : 'border-transparent bg-[var(--bg-root)]'
      }`}
    >
      <div className="flex items-center gap-2">
        <Switch
          checked={on}
          onChange={(e) => onToggle(e.target.checked)}
          title={on ? 'プロンプトに含める（ON）' : 'プロンプトから除外（OFF）'}
        />
        <Label className={`text-xs flex-1 !mb-0 ${on ? '' : 'text-[var(--text-muted)]'}`}>
          {label}
        </Label>
        {!on && (
          <button
            type="button"
            onClick={onToggleExpand}
            className="text-[10px] text-[var(--text-muted)] hover:text-[var(--accent)] px-1"
          >
            {expanded ? '閉じる' : '編集'}
          </button>
        )}
      </div>
      {expanded ? (
        <div className="mt-1.5">{children}</div>
      ) : (
        <p className="text-[10px] text-[var(--text-muted)] truncate mt-0.5 pl-9">{preview}</p>
      )}
    </div>
  );
}

/**
 * blur 時保存の Input。日本語 IME 安全のため Enter キーでは保存しない。
 * 値変化なしの場合は書き込まない。
 */
function BlurInput({
  value,
  onSave,
  type = 'text',
  placeholder,
  prefix,
}: {
  value: string;
  onSave: (v: string) => Promise<unknown> | void;
  type?: string;
  placeholder?: string;
  prefix?: string;
}) {
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);
  const composing = useRef(false);

  const handleBlur = () => {
    if (composing.current) return;
    if (local === value) return;
    void onSave(local);
  };

  if (prefix) {
    return (
      <div className="flex items-center border border-[var(--border)] rounded-md bg-[var(--bg-elevated)]">
        <span className="text-xs text-[var(--text-muted)] pl-2">{prefix}</span>
        <input
          type={type}
          value={local}
          placeholder={placeholder}
          onChange={(e) => setLocal(e.target.value)}
          onBlur={handleBlur}
          onCompositionStart={() => (composing.current = true)}
          onCompositionEnd={() => (composing.current = false)}
          className="flex-1 px-2 py-2 bg-transparent text-sm outline-none"
        />
      </div>
    );
  }
  return (
    <Input
      type={type}
      value={local}
      placeholder={placeholder}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={handleBlur}
      onCompositionStart={() => (composing.current = true)}
      onCompositionEnd={() => (composing.current = false)}
    />
  );
}

/**
 * blur 時保存の Textarea。
 *
 * 2026-07-28: 内容に応じて高さが伸びる auto-grow 化。
 * 従来は rows=2 固定で、キャンペーン概要のような複数行の入力が 2 行目で
 * 見切れ、毎回リサイズハンドルを引っ張る必要があった。
 * 上限 (MAX_H) を超えたら内部スクロールに切り替えてパネルが伸びすぎないようにする。
 */
const TEXTAREA_MAX_H = 220;

function BlurTextarea({
  value,
  onSave,
  placeholder,
}: {
  value: string;
  onSave: (v: string) => Promise<unknown> | void;
  placeholder?: string;
}) {
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);
  const composing = useRef(false);
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    const next = Math.min(el.scrollHeight, TEXTAREA_MAX_H);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > TEXTAREA_MAX_H ? 'auto' : 'hidden';
  }, [local]);

  return (
    <Textarea
      ref={ref}
      value={local}
      placeholder={placeholder}
      rows={2}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => {
        if (composing.current) return;
        if (local === value) return;
        void onSave(local);
      }}
      onCompositionStart={() => (composing.current = true)}
      onCompositionEnd={() => (composing.current = false)}
      className="resize-none"
    />
  );
}
